import { Ref } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import Button from "@atlaskit/button-next/new";
import DropdownMenu, { DropdownItemGroup } from "@atlaskit/dropdown-menu";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import Lozenge from "@atlaskit/lozenge-next";
import { Inline } from "@atlaskit/primitives-next";

import {
    getStatusColor,
    getStatusText
} from "@/components/fastshift/src/common/utils";
import {
    FASTSHIFT_ROLL_UP_STATUS,
    FastshiftRollUpStatus
} from "@/libraries/bxp-web-stargate-client/src";

import { FilterOption } from "../../types";
import { messages } from "../messages";

import { StatusFilterDropdownItem } from "./status-filter-dropdown-item/status-filter-dropdown-item";

export const StatusFilterDropdown = ({
    selectedStatus,
    onStatusChange,
    sendStatusFilterDropdownClickUIEvent,
    sendStatusFilterDropdownItemClickUIEvent
}: {
    selectedStatus: FilterOption<FastshiftRollUpStatus> | null;
    onStatusChange: (
        option: FilterOption<FastshiftRollUpStatus> | null
    ) => void;
    sendStatusFilterDropdownClickUIEvent: () => void;
    sendStatusFilterDropdownItemClickUIEvent: (selectedStatus: string) => void;
}) => {
    const { formatMessage } = useIntl();

    const statusOptions: FilterOption<FastshiftRollUpStatus>[] = Object.values(
        FASTSHIFT_ROLL_UP_STATUS
    ).map((status) => {
        const statusMessage = getStatusText(status);
        const statusText = formatMessage(statusMessage);
        return {
            label: statusText,
            value: status
        };
    });

    return (
        <DropdownMenu
            // This is atlaskit's recommended pattern for custom trigger components
            // eslint-disable-next-line react/no-unstable-nested-components
            trigger={({ triggerRef, ...triggerProps }) => (
                <Button
                    ref={triggerRef as Ref<HTMLButtonElement>}
                    {...triggerProps}
                    testId="status-filter-button"
                    onClick={(e) => {
                        sendStatusFilterDropdownClickUIEvent();
                        triggerProps.onClick?.(e);
                    }}
                    isSelected={!!selectedStatus}
                >
                    <Inline
                        space="space.100"
                        alignInline="center"
                        alignBlock="center"
                    >
                        {selectedStatus?.label ? (
                            <Lozenge
                                appearance={getStatusColor(
                                    selectedStatus.value
                                )}
                            >
                                {selectedStatus?.label}
                            </Lozenge>
                        ) : (
                            <FormattedMessage
                                {...messages.migrationTicketsFiltersStatusPlaceholder}
                            />
                        )}
                        <ChevronDownIcon
                            label="Chevron down icon"
                            size="small"
                        />
                    </Inline>
                </Button>
            )}
            spacing="compact"
            shouldRenderToParent
        >
            <DropdownItemGroup>
                {statusOptions.map((option) => (
                    <StatusFilterDropdownItem
                        key={option.value}
                        option={option}
                        isSelected={selectedStatus?.value === option.value}
                        onStatusChange={onStatusChange}
                        sendStatusFilterDropdownItemClickUIEvent={
                            sendStatusFilterDropdownItemClickUIEvent
                        }
                    />
                ))}
            </DropdownItemGroup>
        </DropdownMenu>
    );
};
